import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/layout.css";
import "./styles/sections.css";
import "./styles/transitions.css";
import { createApp } from "vue";
import App from "./App.vue";
import { router } from "./router";
import { initReveal } from "./ui/reveal";
import { initContactForm } from "./ui/contact-form";
import { runBootGate, fontsSettled } from "./ui/boot";
import { cssDurationMs } from "./ui/css-time";
import {
  detectCapabilities,
  prefersReducedMotion,
  type GpuCapabilities,
} from "./scene/capabilities";
// Two small constants, not the scene: importing this does not pull three.js
// in, which is the whole reason the tier decision can be made out here.
import { HORIZON_SEGMENTS, MASSIF_SEGMENTS } from "./scene/heightmap-presets";
import type { SceneHandle } from "./scene";
import type { initScrollRig } from "./scene/scroll-rig";

document.documentElement.classList.add("js");
createApp(App).use(router).mount("#app");

// --------------------------------------------------------------------- //
// The mountain                                                          //
// --------------------------------------------------------------------- //
// One scene for the whole session. It is booted once, below, and from then
// on route changes only tell it whether the climb is on screen - the canvas,
// the terrain and the trail are never rebuilt, which is the entire reason
// the router exists rather than letting each page load its own copy.

let scene: SceneHandle | null = null;
let mountClimb: typeof initScrollRig | null = null;
let unmountClimb: (() => void) | null = null;

/**
 * Brings the mountain into line with whichever route is now mounted.
 *
 * Called from two directions - a route change, and the scene finishing its
 * (asynchronous) boot - because either can be the one that happens last.
 * Doing nothing until both have happened is what makes a click during the
 * boot gate, or a deep link straight onto /services, land in the same
 * state as the ordinary path through the site.
 */
function syncScene(): void {
  if (!scene || !mountClimb) return;

  unmountClimb?.();
  unmountClimb = null;

  if (window.location.pathname !== "/") {
    scene.setClimbing(false, cssDurationMs("--route-scene-return", 900));
    return;
  }

  scene.setClimbing(true);
  unmountClimb = mountClimb(scene.trail, scene.cameraRig);
}

/**
 * Fetches and builds the scene, resolving once it has a frame on screen.
 *
 * No longer deferred to requestIdleCallback. That was right when the page
 * behind it was already visible and the scene was competing with it for the
 * main thread - but the page is now held behind the boot gate until this
 * resolves, so every millisecond spent waiting for idle is a millisecond of
 * blank ground the visitor is looking at.
 *
 * Resolves rather than rejects on every failure path. The gate is waiting on
 * this promise and a visitor whose GPU cannot run the scene still has a site
 * to read.
 */
/**
 * Which baked maps this visitor's tier actually needs.
 *
 * Asked here rather than inside the scene because the fetch has to start
 * before the scene module has even been downloaded - and because this is
 * the one place that already knows the tier without opening a second WebGL
 * context to find out. The tier test is the scene's own, kept in step by
 * importing the segment counts rather than by restating them.
 */
function bakedKeysFor(capabilities: GpuCapabilities): string[] {
  const tier = capabilities.isMobile || capabilities.isSoftwareRenderer ? "low" : "high";
  return [
    `massif-${MASSIF_SEGMENTS[tier] + 1}`,
    `horizon-${HORIZON_SEGMENTS[tier] + 1}`,
  ];
}

async function bootScene(): Promise<void> {
  if (prefersReducedMotion()) return;

  const capabilities = detectCapabilities();
  // Cheap check, no three.js involved yet - skip the download entirely
  // rather than fetch a chunk that can only ever return null.
  if (!capabilities.webgl2) return;

  // Start the terrain's grain map now, from behind both guards above.
  //
  // A <link rel="preload"> in index.html would be simpler and would be
  // wrong: it fires for every visitor, including the reduced-motion and
  // no-WebGL2 ones who are being served the CSS fallback and will never
  // build a scene to put it in. An Image() here costs them nothing and
  // still warms the exact cache entry three's TextureLoader goes on to
  // ask for, so by the time the 400 KB scene chunk has parsed the texture
  // is usually already sitting there. The scene works without this; it
  // just holds the boot gate up longer. See scene/textures.ts.
  new Image().src = `${import.meta.env.BASE_URL}textures/terrain-detail.webp`;

  try {
    // The heightmaps are fetched alongside the code that needs them, not
    // after it: they are static files with no dependency on the three.js
    // chunk, and waiting for the import to land before asking for them
    // would serialise two things the browser can do at once.
    //
    // loadBakedHeightmaps never rejects. Whatever does not arrive is
    // generated in the browser instead, which is what happens on a build
    // where `npm run bake` has not been run.
    const [{ initScene }, { initScrollRig: mount }, baked] = await Promise.all([
      import("./scene"),
      import("./scene/scroll-rig"),
      import("./scene/heightmap-store").then(({ loadBakedHeightmaps }) =>
        loadBakedHeightmaps(bakedKeysFor(capabilities)),
      ),
    ]);

    const handle = initScene(capabilities, baked);
    if (!handle) return;

    scene = handle;
    mountClimb = mount;
    // The photographic stand-in has done its job for this session - and with
    // the canvas persisting across routes, there is no later page load to
    // put it back.
    document.querySelector(".scene-fallback")?.remove();

    syncScene();
    await handle.firstFrame;
  } catch {
    // A failed chunk fetch leaves the CSS fallback in place, which is the
    // same picture a no-WebGL2 visitor gets.
  }
}

// The gate comes down when the scene is on screen AND the webfonts have
// settled - the two things that would otherwise visibly change under the
// visitor a moment after they started reading.
runBootGate(Promise.all([bootScene(), fontsSettled()]));

initReveal();
initContactForm();
window.addEventListener("app:route-change", syncScene);
